/** ONE PORTFOLIO — Client 360°, Analyzer, Insights, Fund deep-dive. */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_FUND, GET_PORTFOLIO } from '../queries';
import { EMPTY, byDesc, rereadPastBackendCache } from './_shared';
import { asClient } from '../clientScope';
import { fundFactors } from './funds';
import { useFactorView, useStyleHoldingsLoader } from './styleFactors';
import {
  assetClassKey, baseRiskRow, classificationAxes, factorContributions, isStressScenario,
} from '../../lib/portfolio';
import { inr, label, pct, signedPct } from '../../lib/format';

/**
 * `exposures` asks for the full classification breakdown — seven axes, each
 * bucket carrying the positions inside it. Off by default because it is the
 * heaviest thing on the record and only one screen draws it: every other caller
 * would be paying for several hundred holding rows it never reads.
 *
 * `forceRefresh` bypasses caching on BOTH sides: it is sent as a GraphQL
 * argument so the backend recomputes rather than answering from its own
 * cache, and it switches the query to `network-only` so Apollo's client-side
 * cache does not just replay an earlier result for the same symbol instead of
 * asking at all. Needed for a symbol whose underlying data can change without
 * the symbol itself changing — a what-if portfolio recreated under the same
 * name run after run being the case this exists for.
 */
/**
 * `clientUserId` reads the portfolio on a client's behalf — the desk's usual
 * `X-Target-User-Id` header (see clientScope.asClient). A client's portfolio
 * is an ordinary portfolio; only who is asking differs.
 *
 * `forceRefetch()` re-reads it past the backend's cache, once, for the screen
 * to call when the portfolio's analysis finishes — see rereadPastBackendCache.
 */
/**
 * `factorHoldings` asks for the per-holding factor contributions — the rows a
 * factor bar opens onto. Off by default because they multiply every factor by
 * the holding count, and only the screen that draws that drill needs them.
 */
export function usePortfolio(securitySymbol, {
  history = true, exposures = false, factorHoldings = false, forceRefresh = false,
  clientUserId = null, skip = false,
} = {}) {
  const context = useMemo(() => asClient(clientUserId), [clientUserId]);
  const { data, loading, error, refetch, client } = useQuery(GET_PORTFOLIO, {
    variables: {
      securitySymbol,
      includeHistorical: history,
      historicalLimit: 260,
      withExposures: exposures,
      withFactorHoldings: factorHoldings,
      forceRefresh,
    },
    context,
    skip: skip || !securitySymbol,
    fetchPolicy: forceRefresh ? 'network-only' : 'cache-first',
  });

  const forceRefetch = useCallback(async () => {
    if (!securitySymbol) return null;
    return rereadPastBackendCache(client, {
      query: GET_PORTFOLIO,
      variables: {
        securitySymbol,
        includeHistorical: history,
        historicalLimit: 260,
        withExposures: exposures,
        withFactorHoldings: factorHoldings,
        forceRefresh,
      },
      context,
    });
  }, [client, context, securitySymbol, history, exposures, factorHoldings, forceRefresh]);

  return useMemo(() => {
    const p = data?.getPortfolio?.portfolio ?? null;
    // Always return the same shape — screens read `allocation`, `holdings` and
    // friends before they know whether the portfolio resolved.
    if (!p) {
      return {
        loading,
        error,
        portfolio: null,
        holdings: EMPTY,
        allocation: EMPTY,
        equityDrift: null,
        contributions: new Map(),
        riskContributions: new Map(),
        series: EMPTY,
        refetch,
        forceRefetch,
        factors: EMPTY,
        benchmark: null,
        risk: null,
        riskScenarios: EMPTY,
        stressScenarios: EMPTY,
        realizedRisk: null,
        realizedRiskContributions: EMPTY,
        riskContributionRows: EMPTY,
        contributionsFor: () => EMPTY,
        returns: null,
        marketValue: 0,
      };
    }

    const holdings = (p.holdings ?? EMPTY)
      .map((h) => ({ ...h, weightPct: (Number(h.weight) || 0) * 100 }))
      .sort(byDesc((h) => h.marketValue));

    /* Allocation vs the goal's policy portfolio — the drift the design leads
       with. Both sides are keyed through `assetClassKey`, because the holdings
       say `fixed_income` where the policy says `Fixed Income`; see the note
       there for what matching them raw did to the drift. */
    const targets = new Map(
      (p.benchmarkPortfolio?.allocations ?? EMPTY).map((a) => [assetClassKey(a.assetClassName), {
        /* The policy's own spelling is the one to show: "Fixed Income", not
           the security master's "fixed_income". */
        label: a.assetClassName,
        target: (Number(a.weight) || 0) * 100,
      }]),
    );
    const actualAlloc = (p.classifications?.assetClasses ?? EMPTY).map((c) => ({
      key: assetClassKey(c.classificationValue),
      label: targets.get(assetClassKey(c.classificationValue))?.label || label(c.classificationValue),
      actual: (Number(c.weight) || 0) * 100,
      target: targets.get(assetClassKey(c.classificationValue))?.target ?? null,
      value: Number(c.marketValue) || 0,
    }));
    // A target with no matching holding is still a gap worth showing.
    targets.forEach(({ label: name, target }, key) => {
      if (!actualAlloc.some((a) => a.key === key)) {
        actualAlloc.push({ key, label: name, actual: 0, target, value: 0 });
      }
    });
    const allocation = actualAlloc
      .map((a) => ({ ...a, drift: a.target == null ? null : a.actual - a.target }))
      .sort((a, b) => b.actual - a.actual);

    const equity = allocation.find((a) => /equity/i.test(a.label));

    const contributions = new Map(
      (p.holdingReturnContributions ?? EMPTY).map((c) => [c.holdingSecuritySymbol, c]),
    );
    /* Contributions come nested under the scenario they belong to, so the
       holdings a screen shows and the metrics row above them are the same run
       by construction. The flat top-level field is one scenario only — see the
       note on RISK_CONTRIBUTION in api/queries/portfolios.js. */
    const contributionsByScenario = new Map(
      (p.riskMetrics ?? EMPTY).map((r) => [r.scenarioValue, r.holdingRiskContributions ?? EMPTY]),
    );

    const series = (p.historicalData ?? EMPTY)
      .filter((h) => h?.snapshotDate)
      .map((h) => ({
        date: h.snapshotDate,
        value: Number(h.marketValue) || 0,
        ret: Number(h.dailyReturn) || 0,
        vol: Number(h.volatility) || null,
        sharpe: Number(h.sharpeRatio) || null,
      }))
      .sort((a, b) => (a.date < b.date ? -1 : 1));

    /* riskMetrics carries one row per configured scenario. The headline
       figures come from the base row — the run with no stress applied — while
       the full list is what the risk tab tabulates. */
    const riskRows = (p.riskMetrics ?? EMPTY).slice().sort((a, b) =>
      String(b.latestDate || b.runDate || '').localeCompare(String(a.latestDate || a.runDate || '')),
    );
    const baseRisk = baseRiskRow(riskRows);
    /* Stress windows by the backend's own rule — riskType, which it derives
       from the scenario. "Everything that is not the base row" would sweep in
       any other parameter configuration alongside them. */
    const stressRows = riskRows.filter(isStressScenario);
    const baseContributions = baseRisk
      ? contributionsByScenario.get(baseRisk.scenarioValue) ?? EMPTY
      : EMPTY;
    const riskContributions = new Map(
      baseContributions.map((c) => [c.holdingSecuritySymbol, c]),
    );

    const factors = fundFactors(p.factorExposures);

    return {
      loading,
      error,
      refetch,
      forceRefetch,
      portfolio: p,
      holdings,
      allocation,
      equityDrift: equity?.drift ?? null,
      contributions,
      riskContributions,
      series,
      factors,
      benchmark: p.benchmarkPortfolio ?? null,
      /** Headline risk — the base-case run. */
      risk: baseRisk,
      /** Every scenario the engine priced, newest run first. */
      riskScenarios: riskRows,
      /** Just the stressed runs, for the scenario table. */
      stressScenarios: stressRows,
      /** Realised (backward-looking) risk, alongside the forecast. */
      realizedRisk: p.realizedRiskMetrics ?? null,
      /**
       * The same realised pass, position by position. Selected by the query all
       * along and dropped here, so it crossed the wire on every load and reached
       * nothing.
       *
       * Unlike `riskContributionRows` these are NOT slices of the fund's risk:
       * the realised pass measures each holding on its own price series and
       * stores it unweighted, so they neither sum to the fund's figure nor carry
       * a weight. Whatever shows them has to say so.
       */
      realizedRiskContributions: p.holdingRealizedRiskContributions ?? EMPTY,
      /** The base case's own holdings — what the headline figures decompose into. */
      riskContributionRows: baseContributions,
      /** One scenario's holdings, by the `scenarioValue` both tables key on. */
      contributionsFor: (scenarioValue) => contributionsByScenario.get(scenarioValue) ?? EMPTY,
      returns: p.returnMetrics ?? null,
      marketValue: Number(p.marketData?.marketValue) || 0,
    };
  }, [data, loading, error, refetch, forceRefetch]);
}

/**
 * ONE PORTFOLIO'S EXPOSURES, three ways — what the Exposures tab's sub-tabs
 * read, composed from the hooks that already read them:
 *
 *   sector      usePortfolio with the classification breakdown, as axes
 *   style       useFactorView's style snapshot, plus a lazy loader for the
 *               holdings behind an opened exposure (computed from the current
 *               book, so a portfolio booked today has them)
 *   regression  useFactorView's loadings against the Top-500, plus usePortfolio's
 *               per-holding factor contributions for the drill
 *
 * Only the view on screen is asked for: Sector never fetches the factor view,
 * Style never fetches the record, and the per-holding factor rows — the
 * heaviest part of a portfolio record — only travel for Regression.
 *
 * `kind: 'fund'` reads a SCHEME instead. A scheme is not a portfolio —
 * `getPortfolio` answers "Portfolio not found" for every fund symbol — so its
 * buckets and per-holding factor rows come off the scheme master (GET_FUND,
 * which the fund page has already cached), its loadings off the fund factor
 * view, and an opened style exposure lists the stored contributions. A model
 * is a portfolio and takes the default.
 */
export function usePortfolioExposures(securitySymbol, {
  view = 'sector', clientUserId = null, kind = 'portfolio',
} = {}) {
  const isFund = kind === 'fund';
  const pf = usePortfolio(securitySymbol, {
    history: false,
    exposures: view === 'sector',
    factorHoldings: view === 'regression',
    clientUserId,
    skip: isFund || view === 'style',
  });
  const fund = useQuery(GET_FUND, {
    variables: { symbol: securitySymbol },
    skip: !isFund || view === 'style' || !securitySymbol,
    fetchPolicy: 'cache-first',
  });
  const factorView = useFactorView(isFund ? 'fund' : 'portfolio', securitySymbol, {
    skip: view === 'sector', clientUserId,
  });
  const loadHoldings = useStyleHoldingsLoader(isFund ? 'stored' : 'analysis', securitySymbol);
  const p = isFund ? fund.data?.getMetaMutualFund ?? null : pf.portfolio;
  const recordLoading = isFund ? fund.loading : pf.loading;
  const recordError = isFund ? fund.error : pf.error;
  const refetchRecord = isFund ? fund.refetch : pf.refetch;

  return useMemo(() => ({
    sector: {
      /* The same classification type on both records, under two names. */
      axes: classificationAxes(isFund ? p?.classifications : p?.exposures),
      loading: recordLoading,
      error: recordError,
    },
    style: {
      styleFactors: factorView.styleFactors,
      loadHoldings,
      loaded: factorView.loaded,
      loading: factorView.loading,
      error: factorView.error,
    },
    regression: {
      factorExposures: factorView.factorExposures,
      benchmarkFactorExposures: factorView.benchmarkFactorExposures,
      contributions: factorContributions(p),
      loaded: factorView.loaded,
      loading: factorView.loading,
      error: factorView.error,
      /* For a screen that writes the per-holding rows and reads them back. */
      refetch: refetchRecord,
    },
  }), [isFund, p, recordLoading, recordError, refetchRecord, factorView, loadHoldings]);
}

/**
 * What's working / not working — holdings ranked by their 1-year return
 * contribution. Fund deep-dive and Deep Insights both render this; deriving it
 * twice had already let the two copies drift (one carried the weight, one not).
 */
export function usePortfolioContributors(pf) {
  const contributors = useMemo(
    () =>
      pf.holdings
        .map((h) => {
          const c = pf.contributions.get(h.holdingSecuritySymbol);
          const v = Number(c?.oneYearReturnContribution ?? c?.ytdReturnContribution);
          return Number.isFinite(v) ? { name: h.name || h.holdingSecuritySymbol, value: v, weight: h.weightPct } : null;
        })
        .filter(Boolean)
        .sort((a, b) => b.value - a.value),
    [pf.holdings, pf.contributions],
  );
  return {
    contributors,
    max: Math.max(0.0001, ...contributors.map((c) => Math.abs(c.value))),
  };
}


/**
 * How concentrated the forecast risk is — the biggest few holdings by
 * volatility × weight, which is the share of risk each position brings.
 *
 * `holdings` is passed rather than taken off `pf` because a scheme's positions
 * come off its scheme master while the risk rows come off `getPortfolio`, and
 * only the holdings side carries a weight for one. Both weights are fractions,
 * null-checked before Number() — `Number(null)` is 0 and would read as a real
 * zero position.
 *
 * `total` is the sum over EVERY decomposed holding, not over the few the donut
 * draws. Summing the top five and calling a row's share of that its share of
 * forecast risk overstates every slice, and by more the longer the tail.
 */
export function usePortfolioRiskConcentration(pf, { holdings } = {}) {
  return useMemo(() => {
    const positions = holdings ?? pf.holdings;
    const weights = new Map(positions.map((h) => [h.holdingSecuritySymbol, h.weight]));
    const rows = pf.riskContributionRows
      .map((r) => {
        const vol = Number(r.forecastVolatility);
        const own = r.weight == null ? weights.get(r.holdingSecuritySymbol) : r.weight;
        const weight = own == null ? null : Number(own);
        if (!Number.isFinite(vol) || !Number.isFinite(weight)) return null;
        return { symbol: r.holdingSecuritySymbol, label: r.name || r.holdingSecuritySymbol, value: vol * weight };
      })
      .filter(Boolean)
      .sort((a, b) => b.value - a.value);
    return { rows, total: rows.reduce((t, r) => t + r.value, 0) };
  }, [pf.riskContributionRows, pf.holdings, holdings]);
}

/** Market shocks priced off the subject's own beta and equity weight. */
export const MARKET_SHOCKS = [
  { label: 'Market −15% correction', equity: -0.15 },
  { label: '2020-style crash (−35% equity)', equity: -0.35 },
  { label: 'Equity rally +12%', equity: 0.12 },
  { label: 'Small/mid de-rating −25%', equity: -0.25, equityOnly: /small|mid/i },
];

/**
 * The same four shocks in rupees: exposed value × shock × beta.
 *
 * A first-order parametric estimate, not a revaluation — it answers "what does
 * a market move do to this book" for shocks the engine has no configured run
 * for. The Studio prices real trades through the backend's scenario engine.
 */
export function useMarketShocks(pf) {
  return useMemo(() => {
    if (!pf.portfolio) return EMPTY;
    const equityWeight = (pf.allocation.find((a) => /equity/i.test(a.label))?.actual ?? 0) / 100;
    const beta = Number(pf.risk?.betaToBenchmark) || 1;
    const mv = pf.marketValue;
    return MARKET_SHOCKS.map((s) => {
      const exposed = s.equityOnly
        ? pf.holdings.filter((h) => s.equityOnly.test(h.name || '')).reduce((t, h) => t + (h.marketValue || 0), 0)
        : mv * equityWeight;
      const impact = exposed * s.equity * beta;
      return { ...s, beta, impact, after: mv + impact, exposed };
    });
  }, [pf.portfolio, pf.allocation, pf.risk, pf.holdings, pf.marketValue]);
}

/**
 * Health score: four graded components, averaged — every input is live.
 * The Analyzer's headline.
 */
export function usePortfolioHealth(pf) {
  return useMemo(() => {
    if (!pf.portfolio) return null;
    const risk = pf.risk || {};
    const holdings = pf.holdings;
    const top = Math.max(0, ...holdings.map((h) => h.weightPct || 0));
    const drift = Math.max(0, ...pf.allocation.filter((a) => a.drift != null).map((a) => Math.abs(a.drift)));

    const ret = Number(pf.returns?.annualizedReturn1y ?? pf.returns?.oneYearReturn);
    const bench = Number(pf.benchmark?.portfolio?.returnMetrics?.oneYearReturn);
    const excess = Number.isFinite(ret) && Number.isFinite(bench) ? (ret - bench) * 100 : null;

    const grade = (v, bands) => {
      if (v == null || !Number.isFinite(v)) return { letter: '—', score: 50, tone: 'grey' };
      if (v <= bands[0]) return { letter: 'A', score: 92, tone: 'green' };
      if (v <= bands[1]) return { letter: 'B', score: 76, tone: 'green' };
      if (v <= bands[2]) return { letter: 'C', score: 58, tone: 'amber' };
      return { letter: 'D', score: 34, tone: 'red' };
    };

    const returns = excess == null
      ? { letter: '—', score: 50, tone: 'grey' }
      : excess >= 2 ? { letter: 'A', score: 92, tone: 'green' }
        : excess >= 0 ? { letter: 'B', score: 76, tone: 'green' }
          : excess >= -2 ? { letter: 'C', score: 58, tone: 'amber' }
            : { letter: 'D', score: 34, tone: 'red' };

    const riskGrade = grade(risk.volatility == null ? null : risk.volatility * 100, [10, 15, 20]);
    const concentration = grade(top, [15, 25, 35]);
    const driftGrade = grade(drift, [3, 6, 10]);

    const score = Math.round((returns.score + riskGrade.score + concentration.score + driftGrade.score) / 4);
    return {
      score,
      label: score >= 80 ? '/100 · Strong' : score >= 60 ? '/100 · Fair' : '/100 · Needs work',
      parts: [
        ['Returns', returns],
        ['Risk', riskGrade],
        ['Concentration', concentration],
        ['Drift', driftGrade],
      ],
      top,
      drift,
      excess,
    };
  }, [pf]);
}

/**
 * The Analyzer's findings — derived, ranked by severity, and each one names
 * its numbers. `health` is the usePortfolioHealth result for the same pf.
 */
export function useAnalyzerFindings(pf, health) {
  return useMemo(() => {
    if (!pf.portfolio) return [];
    const out = [];
    const risk = pf.risk || {};

    pf.allocation
      .filter((a) => a.drift != null && Math.abs(a.drift) > 5)
      .forEach((a) => {
        out.push({
          severity: Math.abs(a.drift) > 10 ? 'High' : 'Medium',
          icon: 'warn',
          title: `${a.label} is ${signedPct(a.drift, 0)} off policy target`,
          body: `Currently ${pct(a.actual, 0)} against a ${pct(a.target, 0)} target. On ${inr(pf.marketValue)} that is ${inr((Math.abs(a.drift) / 100) * pf.marketValue)} sitting in the wrong sleeve.`,
        });
      });

    const heavy = pf.holdings.filter((h) => h.weightPct > 25);
    heavy.forEach((h) => {
      out.push({
        severity: 'High',
        icon: 'view',
        title: `Concentration: ${h.name || h.holdingSecuritySymbol} is ${pct(h.weightPct, 0)} of the portfolio`,
        body: `A single holding above a quarter of the book makes portfolio outcomes hostage to one manager. Trimming to 15% frees ${inr(((h.weightPct - 15) / 100) * pf.marketValue)}.`,
      });
    });

    if (risk.maxDrawdown != null && risk.maxDrawdown < -0.15) {
      out.push({
        severity: risk.maxDrawdown < -0.25 ? 'High' : 'Medium',
        icon: 'down',
        title: `Max drawdown ${pct(risk.maxDrawdown * 100)} on record`,
        body: `The deepest peak-to-trough loss this portfolio has taken. On today's value that is ${inr(Math.abs(risk.maxDrawdown) * pf.marketValue)}.`,
      });
    }

    if (risk.trackingError != null && risk.trackingError > 0.08) {
      out.push({
        severity: 'Medium',
        icon: 'warn',
        title: `Tracking error ${pct(risk.trackingError * 100)} vs the policy benchmark`,
        body: 'The portfolio is taking materially different bets from its mandate. Worth confirming those bets are deliberate.',
      });
    }

    const losers = pf.holdings.filter((h) => Number(h.unrealizedPnlPercent) < -10);
    losers.slice(0, 2).forEach((h) => {
      out.push({
        severity: 'Medium',
        icon: 'down',
        title: `${h.name || h.holdingSecuritySymbol} is ${pct(h.unrealizedPnlPercent)} underwater`,
        body: `${inr(h.marketValue)} held against ${inr(h.totalCost)} cost. Harvesting the loss can offset gains elsewhere this financial year.`,
      });
    });

    if (health?.excess != null && health.excess > 0) {
      out.push({
        severity: 'Opp.',
        icon: 'up',
        title: `Beating the policy benchmark by ${pct(health.excess)} over 1 year`,
        body: 'The excess is real — the question the Attribution and Exposures tabs answer is whether it comes from selection skill or a single factor bet.',
      });
    }

    const cash = pf.allocation.find((a) => /cash|liquid|money/i.test(a.label));
    if (cash && cash.actual > 5) {
      out.push({
        severity: 'Opp.',
        icon: 'coin',
        title: `${pct(cash.actual, 0)} sitting in cash`,
        body: `${inr(cash.value)} is uninvested. An arbitrage or liquid fund keeps the liquidity and adds yield.`,
      });
    }

    const order = { High: 0, Medium: 1, 'Opp.': 2 };
    return out.sort((a, b) => order[a.severity] - order[b.severity]);
  }, [pf, health]);
}

/**
 * The trades the analyzer would draft for a proposal: trim what is oversized,
 * harvest what is deeply underwater.
 */
export function useSuggestedTrades(pf) {
  return useMemo(() => {
    if (!pf.portfolio) return [];
    const out = [];
    pf.holdings.forEach((h) => {
      if (h.weightPct > 25) {
        const trimPct = h.weightPct - 15;
        out.push({
          symbol: h.holdingSecuritySymbol,
          name: h.name || h.holdingSecuritySymbol,
          action: 'Trim',
          reason: `${pct(h.weightPct, 0)} of the book — above the 25% concentration guard`,
          amount: (trimPct / 100) * pf.marketValue,
          units: (Number(h.quantity) || 0) * (trimPct / h.weightPct),
          price: Number(h.price) || 0,
        });
      } else if (Number(h.unrealizedPnlPercent) < -10) {
        out.push({
          symbol: h.holdingSecuritySymbol,
          name: h.name || h.holdingSecuritySymbol,
          action: 'Harvest loss',
          reason: `${pct(h.unrealizedPnlPercent)} underwater — realising the loss offsets gains this FY`,
          amount: Number(h.marketValue) || 0,
          units: Number(h.quantity) || 0,
          price: Number(h.price) || 0,
        });
      }
    });
    return out;
  }, [pf]);
}
